import { useAuth0 } from "@auth0/auth0-react";
import React, { useState } from "react";
import {
  GetGroupsDetailsDocument,
  useAddContextToGroupMutation,
  useAddPostToContextMutation,
  useEditContextMutation,
  useEditPostMutation,
  useGetGroupsDetailsQuery,
} from "../generated/graphql";
import { parsePostId } from "../utils/idHelpers";
import ErrorMessage from "./ErrorMessage";
import Toast from "./Toast";
import { useToast } from "../hooks/useToast";

type UnifiedPost = {
  rawPostId: string;
  name: string;
  enabled: boolean;
  groupIds: string[];
};

type UnifiedContext = {
  id: string;
  name: string;
  enabled: boolean;
  groupIds: string[];
  posts: UnifiedPost[];
};

function mergeGroups(
  groups: { id: string; name: string; contexts: { id: string; name: string; enabled: boolean; posts: { id: string; name: string; enabled: boolean }[] }[] }[]
): UnifiedContext[] {
  const contextMap = new Map<string, UnifiedContext>();

  for (const group of groups) {
    for (const context of group.contexts) {
      const existing = contextMap.get(context.id);
      if (!existing) {
        contextMap.set(context.id, {
          id: context.id,
          name: context.name,
          enabled: context.enabled,
          groupIds: [group.id],
          posts: context.posts.map((post) => {
            const { postId: rawPostId } = parsePostId(post.id);
            return { rawPostId, name: post.name, enabled: post.enabled, groupIds: [group.id] };
          }),
        });
      } else {
        existing.groupIds.push(group.id);
        if (context.enabled) existing.enabled = true;
        for (const post of context.posts) {
          const { postId: rawPostId } = parsePostId(post.id);
          const existingPost = existing.posts.find((p) => p.rawPostId === rawPostId);
          if (!existingPost) {
            existing.posts.push({ rawPostId, name: post.name, enabled: post.enabled, groupIds: [group.id] });
          } else {
            existingPost.groupIds.push(group.id);
            if (post.enabled) existingPost.enabled = true;
          }
        }
      }
    }
  }

  return Array.from(contextMap.values()).sort((a, b) => a.name.localeCompare(b.name, "fr"));
}

const REFETCH = [{ query: GetGroupsDetailsDocument }];

const Categories: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const { loading, data, error, refetch } = useGetGroupsDetailsQuery({ skip: !isAuthenticated });

  const [editContextMutation, { loading: savingContext }] = useEditContextMutation();
  const [editPostMutation, { loading: savingPost }] = useEditPostMutation();
  const [addContextMutation, { loading: addingContext }] = useAddContextToGroupMutation();
  const [addPostMutation, { loading: addingPost }] = useAddPostToContextMutation();

  const [showInactive, setShowInactive] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const [editingContextId, setEditingContextId] = useState<string | null>(null);
  const [editingContextName, setEditingContextName] = useState("");

  const [editingPostKey, setEditingPostKey] = useState<string | null>(null); // contextId+rawPostId
  const [editingPostName, setEditingPostName] = useState("");

  const [addingPostToContextId, setAddingPostToContextId] = useState<string | null>(null);
  const [newPostName, setNewPostName] = useState("");

  const [showAddContext, setShowAddContext] = useState(false);
  const [newContextName, setNewContextName] = useState("");
  const [newContextGroupIds, setNewContextGroupIds] = useState<string[]>([]);

  const { toast, setToast } = useToast();

  if (authLoading || loading) {
    return <div className="text-center text-gray-500 mt-8">Chargement...</div>;
  }

  if (!isAuthenticated) {
    return <div className="text-center text-red-500 mt-8">Veuillez vous connecter pour accéder aux catégories.</div>;
  }

  if (error) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Catégories</h1>
        <ErrorMessage error={error} onRetry={() => refetch()} title="Erreur lors du chargement des catégories" />
      </div>
    );
  }

  const groups = data?.groups ?? [];
  const allContexts = mergeGroups(groups);
  const visibleContexts = showInactive ? allContexts : allContexts.filter((c) => c.enabled);

  const groupName = (groupId: string) => groups.find((g) => g.id === groupId)?.name ?? groupId;

  const toggleExpand = (contextId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(contextId) ? next.delete(contextId) : next.add(contextId);
      return next;
    });
  };

  const handleToggleContext = async (ctx: UnifiedContext) => {
    try {
      await Promise.all(
        ctx.groupIds.map((groupId) =>
          editContextMutation({
            variables: { groupId, contextId: ctx.id, name: ctx.name, enabled: !ctx.enabled },
            refetchQueries: REFETCH,
          })
        )
      );
    } catch {
      setToast({ message: "Erreur lors de la mise à jour", type: "error" });
    }
  };

  const handleSaveContextName = async (ctx: UnifiedContext) => {
    const name = editingContextName.trim();
    if (!name) return;
    try {
      await Promise.all(
        ctx.groupIds.map((groupId) =>
          editContextMutation({
            variables: { groupId, contextId: ctx.id, name, enabled: ctx.enabled },
            refetchQueries: REFETCH,
          })
        )
      );
      setEditingContextId(null);
      setToast({ message: "Catégorie mise à jour", type: "success" });
    } catch {
      setToast({ message: "Erreur lors de la mise à jour", type: "error" });
    }
  };

  const handleTogglePost = async (ctx: UnifiedContext, post: UnifiedPost) => {
    try {
      await Promise.all(
        post.groupIds.map((groupId) =>
          editPostMutation({
            variables: { groupId, contextId: ctx.id, postId: post.rawPostId, name: post.name, enabled: !post.enabled },
            refetchQueries: REFETCH,
          })
        )
      );
    } catch {
      setToast({ message: "Erreur lors de la mise à jour", type: "error" });
    }
  };

  const handleSavePostName = async (ctx: UnifiedContext, post: UnifiedPost) => {
    const name = editingPostName.trim();
    if (!name) return;
    try {
      await Promise.all(
        post.groupIds.map((groupId) =>
          editPostMutation({
            variables: { groupId, contextId: ctx.id, postId: post.rawPostId, name, enabled: post.enabled },
            refetchQueries: REFETCH,
          })
        )
      );
      setEditingPostKey(null);
      setToast({ message: "Poste mis à jour", type: "success" });
    } catch {
      setToast({ message: "Erreur lors de la mise à jour", type: "error" });
    }
  };

  const handleAddPost = async (ctx: UnifiedContext) => {
    const name = newPostName.trim();
    if (!name) return;
    const postId = crypto.randomUUID();
    try {
      await Promise.all(
        ctx.groupIds.map((groupId) =>
          addPostMutation({
            variables: { groupId, contextId: ctx.id, postId, name },
            refetchQueries: REFETCH,
          })
        )
      );
      setAddingPostToContextId(null);
      setNewPostName("");
      setToast({ message: "Poste ajouté", type: "success" });
    } catch {
      setToast({ message: "Erreur lors de l'ajout", type: "error" });
    }
  };

  const handleAddContext = async () => {
    const name = newContextName.trim();
    if (!name || newContextGroupIds.length === 0) return;
    const contextId = crypto.randomUUID();
    try {
      await Promise.all(
        newContextGroupIds.map((groupId) =>
          addContextMutation({
            variables: { groupId, name, contextId },
            refetchQueries: REFETCH,
          })
        )
      );
      setShowAddContext(false);
      setNewContextName("");
      setToast({ message: "Catégorie ajoutée", type: "success" });
    } catch {
      setToast({ message: "Erreur lors de l'ajout", type: "error" });
    }
  };

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="container mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Catégories</h1>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded-sm"
              />
              Afficher les inactifs
            </label>
            <button
              onClick={() => {
                setShowAddContext(true);
                setNewContextGroupIds(groups.map((g) => g.id));
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              + Nouvelle catégorie
            </button>
          </div>
        </div>

        {showAddContext && (
          <div className="bg-white rounded-lg shadow-xs border border-blue-200 p-4 mb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Nouvelle catégorie</h3>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="block text-xs text-gray-600 mb-1">Nom</label>
                <input
                  type="text"
                  value={newContextName}
                  onChange={(e) => setNewContextName(e.target.value)}
                  placeholder="Ex: Alimentation"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                  onKeyDown={(e) => e.key === "Enter" && handleAddContext()}
                  autoFocus
                />
              </div>
              {groups.length > 1 && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Foyers</label>
                  <div className="flex flex-col gap-1">
                    {groups.map((g) => (
                      <label key={g.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newContextGroupIds.includes(g.id)}
                          onChange={(e) =>
                            setNewContextGroupIds((prev) =>
                              e.target.checked ? [...prev, g.id] : prev.filter((id) => id !== g.id)
                            )
                          }
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded-sm"
                        />
                        {g.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <button
                onClick={handleAddContext}
                disabled={addingContext || !newContextName.trim() || newContextGroupIds.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {addingContext ? "Ajout..." : "Ajouter"}
              </button>
              <button
                onClick={() => { setShowAddContext(false); setNewContextName(""); setNewContextGroupIds([]); }}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Annuler
              </button>
            </div>
          </div>
        )}

        {visibleContexts.length === 0 ? (
          <div className="bg-white rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-500">
            <p>{allContexts.length === 0 ? "Aucune catégorie pour le moment." : "Aucune catégorie active. Activez « Afficher les inactifs » pour les voir."}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {visibleContexts.map((ctx) => {
              const expanded = expandedIds.has(ctx.id);
              const isEditingCtx = editingContextId === ctx.id;
              const visiblePosts = showInactive ? ctx.posts : ctx.posts.filter((p) => p.enabled);

              return (
                <div
                  key={ctx.id}
                  className={`bg-white rounded-lg shadow-xs border ${ctx.enabled ? "border-gray-200" : "border-gray-100 opacity-60"}`}
                >
                  {/* Context row */}
                  <div className="flex items-center gap-3 px-4 py-3">
                    <button
                      onClick={() => toggleExpand(ctx.id)}
                      className="text-gray-400 hover:text-gray-600 w-4 text-center shrink-0"
                    >
                      {expanded ? "▼" : "▶"}
                    </button>

                    {isEditingCtx ? (
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          type="text"
                          value={editingContextName}
                          onChange={(e) => setEditingContextName(e.target.value)}
                          className="flex-1 px-2 py-1 border border-blue-400 rounded-sm text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveContextName(ctx);
                            if (e.key === "Escape") setEditingContextId(null);
                          }}
                          autoFocus
                        />
                        <button
                          onClick={() => handleSaveContextName(ctx)}
                          disabled={savingContext}
                          className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded-sm hover:bg-blue-700 disabled:opacity-50"
                        >
                          {savingContext ? "..." : "OK"}
                        </button>
                        <button
                          onClick={() => setEditingContextId(null)}
                          className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-sm hover:bg-gray-200"
                        >
                          Annuler
                        </button>
                      </div>
                    ) : (
                      <span
                        className="font-medium text-gray-800 flex-1 cursor-pointer"
                        onClick={() => toggleExpand(ctx.id)}
                      >
                        {ctx.name}
                      </span>
                    )}

                    {/* Foyer badges */}
                    {groups.length > 1 && (
                      <div className="flex gap-1">
                        {ctx.groupIds.map((gId) => (
                          <span
                            key={gId}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700"
                          >
                            {groupName(gId)}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Actions */}
                    {!isEditingCtx && (
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => {
                            setEditingContextId(ctx.id);
                            setEditingContextName(ctx.name);
                          }}
                          className="px-2 py-1 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-colors"
                          title="Renommer"
                        >
                          ✎
                        </button>
                        <button
                          onClick={() => handleToggleContext(ctx)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-hidden ${ctx.enabled ? "bg-blue-600" : "bg-gray-300"}`}
                          title={ctx.enabled ? "Désactiver" : "Activer"}
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${ctx.enabled ? "translate-x-4" : "translate-x-0.5"}`}
                          />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Posts */}
                  {expanded && (
                    <div className="border-t border-gray-100 pb-2">
                      {visiblePosts.length === 0 && !addingPostToContextId && (
                        <div className="p-4 bg-gray-50 rounded-sm border border-gray-200"><p className="text-gray-500 italic">Aucun poste actif.</p></div>
                      )}
                      {visiblePosts.map((post) => {
                        const postKey = `${ctx.id}/${post.rawPostId}`;
                        const isEditingPost = editingPostKey === postKey;

                        return (
                          <div
                            key={post.rawPostId}
                            className={`flex items-center gap-3 px-10 py-2 hover:bg-gray-50 ${!post.enabled ? "opacity-50" : ""}`}
                          >
                            <span className="text-gray-300 text-xs">•</span>

                            {isEditingPost ? (
                              <div className="flex items-center gap-2 flex-1">
                                <input
                                  type="text"
                                  value={editingPostName}
                                  onChange={(e) => setEditingPostName(e.target.value)}
                                  className="flex-1 px-2 py-1 border border-blue-400 rounded-sm text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSavePostName(ctx, post);
                                    if (e.key === "Escape") setEditingPostKey(null);
                                  }}
                                  autoFocus
                                />
                                <button
                                  onClick={() => handleSavePostName(ctx, post)}
                                  disabled={savingPost}
                                  className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded-sm hover:bg-blue-700 disabled:opacity-50"
                                >
                                  {savingPost ? "..." : "OK"}
                                </button>
                                <button
                                  onClick={() => setEditingPostKey(null)}
                                  className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-sm hover:bg-gray-200"
                                >
                                  Annuler
                                </button>
                              </div>
                            ) : (
                              <span className="flex-1 text-sm text-gray-700">{post.name}</span>
                            )}

                            {!isEditingPost && (
                              <div className="flex items-center gap-2 shrink-0">
                                <button
                                  onClick={() => {
                                    setEditingPostKey(postKey);
                                    setEditingPostName(post.name);
                                  }}
                                  className="px-2 py-1 text-xs text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-colors"
                                  title="Renommer"
                                >
                                  ✎
                                </button>
                                <button
                                  onClick={() => handleTogglePost(ctx, post)}
                                  className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors focus:outline-hidden ${post.enabled ? "bg-blue-600" : "bg-gray-300"}`}
                                  title={post.enabled ? "Désactiver" : "Activer"}
                                >
                                  <span
                                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${post.enabled ? "translate-x-4" : "translate-x-0.5"}`}
                                  />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Add post inline form */}
                      {addingPostToContextId === ctx.id ? (
                        <div className="flex items-center gap-2 px-10 py-2">
                          <input
                            type="text"
                            value={newPostName}
                            onChange={(e) => setNewPostName(e.target.value)}
                            placeholder="Nom du poste"
                            className="flex-1 px-2 py-1 border border-blue-400 rounded-sm text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleAddPost(ctx);
                              if (e.key === "Escape") { setAddingPostToContextId(null); setNewPostName(""); }
                            }}
                            autoFocus
                          />
                          <button
                            onClick={() => handleAddPost(ctx)}
                            disabled={addingPost || !newPostName.trim()}
                            className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded-sm hover:bg-blue-700 disabled:opacity-50"
                          >
                            {addingPost ? "..." : "Ajouter"}
                          </button>
                          <button
                            onClick={() => { setAddingPostToContextId(null); setNewPostName(""); }}
                            className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-sm hover:bg-gray-200"
                          >
                            Annuler
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setAddingPostToContextId(ctx.id); setNewPostName(""); }}
                          className="ml-10 mt-1 px-3 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-colors"
                        >
                          + Ajouter un poste
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default Categories;
